import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { consultarItemDifereDaProposta } from '@/hooks/useItemDifereDaProposta';

interface RecuperarItemParams {
  cancelamentoId: string;
  itemId: string;
  observacao?: string;
  /** (Douglas 30/07) Motivo catalogado da volta — é ele que substitui o motivo
   *  do cancelamento na planilha mãe assim que o item é recuperado. */
  motivoRecuperacaoId?: string;
}

/** O que fn_recuperar_item_justificado devolve: onde o item ficou. */
interface ItemRecuperado {
  item_id: string;
  pedido_id: string;
  quadro: string;
  coluna: string;
  historico_id: string | null;
}

export function useRecuperacaoItem() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutateAsync: recuperarItem, isPending } = useMutation({
    mutationFn: async ({ cancelamentoId, itemId, observacao, motivoRecuperacaoId }: RecuperarItemParams) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      if (!observacao || observacao.trim().length < 20) {
        throw new Error('A justificativa deve ter no mínimo 20 caracteres');
      }
      if (observacao.trim().length > 500) {
        throw new Error('A justificativa deve ter no máximo 500 caracteres');
      }

      const diferenca = await consultarItemDifereDaProposta(itemId);
      if (diferenca.difere) {
        throw new Error('A proposta já foi corrigida. Use Revisar Proposta → Recuperar Pedido para levar a correção ao item.');
      }

      // Uma transação só (13/10/2026): o banco fecha o cancelamento, move o item
      // e grava UM registro no histórico, "Item recuperado" com a justificativa.
      // Em três idas pelo navegador a volta gravava também "Pedido recuperado
      // pela revisão da proposta", sem revisão nenhuma, e o aviso ao cliente
      // entrava duas vezes na fila.
      const temCancelamento = !!cancelamentoId && cancelamentoId !== 'undefined';
      const rpc = supabase.rpc.bind(supabase) as unknown as (fn: string, args: Record<string, unknown>) =>
        PromiseLike<{ data: ItemRecuperado | null; error: { message?: string } | null }>;
      const { data: recuperado, error } = await rpc('fn_recuperar_item_justificado', {
        p_item_id: itemId,
        p_observacao: observacao.trim(),
        p_cancelamento_id: temCancelamento ? cancelamentoId : null,
        p_motivo_recuperacao_id: motivoRecuperacaoId ?? null,
      });
      if (error) throw new Error(error.message || 'Não foi possível recuperar o item.');
      if (!recuperado) throw new Error('Não foi possível recuperar o item.');

      // NAVEGAR IMEDIATAMENTE
      const foiParaPreAtivo = recuperado.quadro === 'PRE_ATIVO';
      const quadroUrl = foiParaPreAtivo ? 'pre-ativo' : 'contrato-prime';
      const colunaParam = foiParaPreAtivo ? 'CPF%20%2F%20CNPJ%20DIVERGENTE' : 'PEDIDO%20RECUPERADO';
      navigate(`/kanbans/${quadroUrl}?highlight=${recuperado.pedido_id}&itemId=${itemId}&coluna=${colunaParam}`);

      // ============ CASO 1: Cancelamento com registro ============
      if (temCancelamento) {
        // FIRE-AND-FORGET: Timeline + BKO (não bloqueia navegação)
        // Buscar dados para auditoria em background
        Promise.allSettled([
          supabase.from('itens_pedido').select('produto_id').eq('id', itemId).single(),
          supabase.from('item_cancelamentos').select('motivo:status_catalogo(nome)').eq('id', cancelamentoId).maybeSingle(),
        ]).then(([itemRes, cancelRes]) => {
          const produtoId = (itemRes.status === 'fulfilled' ? itemRes.value.data?.produto_id : null);
          const motivoNome = (cancelRes.status === 'fulfilled' ? (cancelRes.value.data as { motivo?: { nome?: string } | null } | null)?.motivo?.nome : null) || 'Motivo não identificado';

          const registrarTimeline = (produtoNome: string) => {
            supabase.from('movimento').insert({
              entidade: 'pedido',
              entidade_id: recuperado.pedido_id,
              tipo: 'recuperacao',
              campo: 'status',
              de: { status: 'cancelado', item_id: itemId, produto: produtoNome, motivo_titulo: motivoNome, quadro: 'PEDIDO_CANCELADO', coluna: 'PEDIDO CANCELADO' },
              para: { status: 'recuperado', quadro: recuperado.quadro, coluna: recuperado.coluna, recuperado_em: new Date().toISOString(), observacao_recuperacao: observacao.trim() },
              motivo: `Item recuperado. Observação: ${observacao.trim()}`,
              usuario_id: user.id,
              created_at: new Date().toISOString()
            }).then(({ error }) => {
              if (error) console.error('[useRecuperacaoItem] Falha ao registrar timeline de recuperação:', error);
            });

            // Aviso BKO
            supabase.from('pedidos').select('id, numero_pedido, bko_id').eq('id', recuperado.pedido_id).single()
              .then(({ data: pedidoData, error: pedidoError }) => {
                if (pedidoError) {
                  console.error('[useRecuperacaoItem] Falha ao buscar pedido para aviso BKO:', pedidoError);
                  return;
                }
                if (pedidoData?.bko_id) {
                  supabase.rpc('fn_criar_aviso_manual_recuperacao', {
                    p_item_id: itemId,
                    p_pedido_id: pedidoData.id,
                    p_pedido_numero: pedidoData.numero_pedido,
                    p_bko_id: pedidoData.bko_id,
                    p_recuperador_id: user.id,
                    p_observacao: observacao.trim()
                  }).then(({ error: rpcError }) => {
                    if (rpcError) console.error('[useRecuperacaoItem] Falha ao criar aviso BKO:', rpcError);
                  });
                }
              });
          };

          if (produtoId) {
            void Promise.resolve(supabase.from('produtos').select('nome').eq('id', produtoId).single())
              .then(({ data: p }) => registrarTimeline(p?.nome || 'Produto não identificado'))
              .catch(() => registrarTimeline('Produto não identificado'));
          } else {
            registrarTimeline('Produto não identificado');
          }
        });

        return { itemAtualizado: recuperado, foiParaPreAtivo };
      }

      // ============ CASO 2: Cancelamento antigo (sem registro) ============
      // FIRE-AND-FORGET: Timeline + BKO caso 2
      Promise.allSettled([
        supabase.from('itens_pedido').select('produto_id').eq('id', itemId).single(),
        supabase.from('item_cancelamentos').select('motivo_id').eq('item_id', itemId).order('created_at', { ascending: false }).limit(1).maybeSingle(),
      ]).then(([itemRes, cancelRes]) => {
        const produtoId = (itemRes.status === 'fulfilled' ? itemRes.value.data?.produto_id : null);
        const motivoId = (cancelRes.status === 'fulfilled' ? cancelRes.value.data?.motivo_id : null);

        const registrarTimeline2 = (produtoNome: string, motivoNome: string) => {
          supabase.from('movimento').insert({
            entidade: 'pedido',
            entidade_id: recuperado.pedido_id,
            tipo: 'recuperacao',
            campo: 'status',
            de: { status: 'cancelado', item_id: itemId, produto: produtoNome, motivo_titulo: motivoNome, quadro: 'PEDIDO_CANCELADO', coluna: 'PEDIDO CANCELADO' },
            para: { status: 'recuperado', quadro: recuperado.quadro, coluna: recuperado.coluna, recuperado_em: new Date().toISOString(), observacao_recuperacao: observacao.trim() },
            motivo: `Item recuperado (cancelamento antigo). Observação: ${observacao.trim()}`,
            usuario_id: user.id,
            created_at: new Date().toISOString()
          }).then(({ error }) => {
            if (error) console.error('[useRecuperacaoItem] Falha ao registrar timeline (caso 2):', error);
          });

          supabase.from('pedidos').select('id, numero_pedido, bko_id').eq('id', recuperado.pedido_id).single()
            .then(({ data: pedidoData, error: pedidoError }) => {
              if (pedidoError) {
                console.error('[useRecuperacaoItem] Falha ao buscar pedido para aviso BKO (caso 2):', pedidoError);
                return;
              }
              if (pedidoData?.bko_id) {
                supabase.rpc('fn_criar_aviso_manual_recuperacao', {
                  p_item_id: itemId,
                  p_pedido_id: pedidoData.id,
                  p_pedido_numero: pedidoData.numero_pedido,
                  p_bko_id: pedidoData.bko_id,
                  p_recuperador_id: user.id,
                  p_observacao: observacao.trim()
                }).then(({ error: rpcError }) => {
                  if (rpcError) console.error('[useRecuperacaoItem] Falha ao criar aviso BKO (caso 2):', rpcError);
                });
              }
            });
        };

        const fetchMotivo = (pId: string | null, cb: (nome: string) => void) => {
          if (pId) {
            void Promise.resolve(supabase.from('status_catalogo').select('nome').eq('id', pId).single())
              .then(({ data: m }) => cb(m?.nome || 'Cancelamento antigo'))
              .catch(() => cb('Cancelamento antigo'));
          } else {
            cb('Cancelamento antigo');
          }
        };

        if (produtoId) {
          void Promise.resolve(supabase.from('produtos').select('nome').eq('id', produtoId).single())
            .then(({ data: p }) => fetchMotivo(motivoId, (motivoNome) => registrarTimeline2(p?.nome || 'Produto', motivoNome)))
            .catch(() => fetchMotivo(motivoId, (motivoNome) => registrarTimeline2('Produto', motivoNome)));
        } else {
          fetchMotivo(motivoId, (motivoNome) => registrarTimeline2('Produto', motivoNome));
        }
      });

      return { itemAtualizado: recuperado, foiParaPreAtivo };
    },
    onSuccess: (data) => {
      const { foiParaPreAtivo } = data;
      toast({
        title: '✅ Item recuperado com sucesso',
        description: foiParaPreAtivo
          ? 'Item movido para PRÉ-ATIVO → CPF / CNPJ DIVERGENTE'
          : 'Item movido para CONTRATO PRIME → PEDIDO RECUPERADO',
      });

      // Invalidar cache em background (sem await)
      queryClient.invalidateQueries({ queryKey: ['item-completo'] });
      queryClient.invalidateQueries({ queryKey: ['pedido-itens-completo'] });
      queryClient.invalidateQueries({ queryKey: ['pedidos'] });
      queryClient.invalidateQueries({ queryKey: ['itens-pedido'] });
      queryClient.invalidateQueries({ queryKey: ['kanban'] });
      queryClient.invalidateQueries({ queryKey: ['avisos'] });
      // Banner do painel (Douglas 16/06): some na hora ao recuperar
      queryClient.invalidateQueries({ queryKey: ['cancelamentos-pedido'] });
      queryClient.invalidateQueries({ queryKey: ['pedido-detail'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao recuperar item',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    recuperarItem,
    isRecuperando: isPending,
  };
}
